import { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from "lucide-react";
import icCalender from '@/assets/icons/ic-calendar.svg'
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { MonthYearSelector } from "@/components/ui/date-picker/month-year-selector";
import { TimeWheelSelector } from "@/components/ui/date-picker/time-wheel-selector";
import { CalendarGrid } from "@/components/ui/date-picker/calendar-grid";
import { usePopoverPosition } from "@/components/ui/date-picker/use-popover-position";

// Main DateTimePicker
interface DateTimePickerProps {
  value?: Date;
  onChange?: (date: Date) => void;
  onClear?: () => void;
}

export function DateTimePicker({ value, onChange, onClear }: DateTimePickerProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverSide = usePopoverPosition(triggerRef);
  const [selectedDate, setSelectedDate] = useState<Date | null>(value || null);

  const today = new Date();
  const [displayYear, setDisplayYear] = useState(
    value?.getFullYear() ?? today.getFullYear()
  );
  const [displayMonth, setDisplayMonth] = useState(
    value?.getMonth() ?? today.getMonth()
  );

  const [hour, setHour] = useState(value ? value.getHours() % 12 || 12 : 12);
  const [minute, setMinute] = useState(value?.getMinutes() ?? 0);
  const [second, setSecond] = useState(value?.getSeconds() ?? 0);
  const [isPM, setIsPM] = useState((value?.getHours() ?? 0) >= 12);
  const [hasTimeSelected, setHasTimeSelected] = useState(!!value);

  useEffect(() => {
    if (value) {
      setSelectedDate(value);
      setDisplayYear(value.getFullYear());
      setDisplayMonth(value.getMonth());
      const h = value.getHours();
      setHour(h % 12 || 12);
      setMinute(value.getMinutes());
      setSecond(value.getSeconds());
      setIsPM(h >= 12);
      setHasTimeSelected(true);
    }
  }, [value]);

  const handlePrevMonth = () => {
    const prevMonth = displayMonth === 0 ? 11 : displayMonth - 1;
    const prevYear = displayMonth === 0 ? displayYear - 1 : displayYear;
    const now = new Date();
    if (prevYear < now.getFullYear() || (prevYear === now.getFullYear() && prevMonth < now.getMonth())) {
      return;
    }
    setDisplayMonth(prevMonth);
    setDisplayYear(prevYear);
  };

  const handleNextMonth = () => {
    if (displayMonth === 11) {
      setDisplayMonth(0);
      setDisplayYear(displayYear + 1);
    } else {
      setDisplayMonth(displayMonth + 1);
    }
  };

  const handleDayClick = (cell: {
    day: number;
    isCurrentMonth: boolean;
    isPrevMonth: boolean;
    isNextMonth: boolean;
    isDisabled: boolean;
  }) => {
    if (cell.isDisabled) return;
    let targetMonth = displayMonth;
    let targetYear = displayYear;
    if (cell.isPrevMonth) {
      targetMonth = displayMonth === 0 ? 11 : displayMonth - 1;
      if (displayMonth === 0) targetYear = displayYear - 1;
    } else if (cell.isNextMonth) {
      targetMonth = displayMonth === 11 ? 0 : displayMonth + 1;
      if (displayMonth === 11) targetYear = displayYear + 1;
    }
    const newDate = new Date(targetYear, targetMonth, cell.day);
    setSelectedDate(newDate);
    setDisplayMonth(targetMonth);
    setDisplayYear(targetYear);
  };

  const handleTimeSave = () => {
    const baseDate = selectedDate ?? new Date();
    let hours = hour;
    if (isPM && hours < 12) hours += 12;
    if (!isPM && hours === 12) hours = 0;

    const next = new Date(baseDate);
    next.setHours(hours, minute, second);

    if (next < new Date()) {
      return;
    }

    onChange?.(next);
    setOpen(false);
  };

  const formatDisplayValue = () => {
    if (!value) return "";
    const h = value.getHours();
    const displayHour = h % 12 || 12;
    const ampm = h >= 12 ? "PM" : "AM";
    return `${format(value, "yyyy-MM-dd")} ${String(displayHour).padStart(
      2,
      "0"
    )}:${String(value.getMinutes()).padStart(2, "0")}:${String(value.getSeconds()).padStart(2, "0")} ${ampm}`;
  };

  const isSelectedTimeInPast = (() => {
    if (!selectedDate || !hasTimeSelected) return false;
    let hours = hour;
    if (isPM && hours < 12) hours += 12;
    if (!isPM && hours === 12) hours = 0;
    const candidate = new Date(selectedDate);
    candidate.setHours(hours, minute, second);
    return candidate < new Date();
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          className="relative w-[490px]"
          onKeyDown={(e) => {
            if ((e.key === "Delete" || e.key === "Backspace") && value) {
              e.preventDefault();
              setSelectedDate(null);
              setHasTimeSelected(false);
              setHour(12);
              setMinute(0);
              setSecond(0);
              setIsPM(false);
              onClear?.();
            }
          }}
        >
          <Input
            readOnly
            value={formatDisplayValue()}
            placeholder="YYYY-MM-DD HH:MM"
            className="pr-10" 
          />
          <img src={icCalender} alt="" className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-light cursor-pointer"/>
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="w-auto p-0 shadow-lg rounded border border-neutral-light/10"
        style={{
          boxShadow:
            "0px 8px 16px rgba(0,0,0,0.09), 0px 0px 36px rgba(0,0,0,0.11)",
          backdropFilter: "blur(100px)",
        }}
        align="start"
        side={popoverSide}
      >
        <div className="p-4 w-[428px]">
          {/* Header with MonthYearSelector */}
          <div className="flex items-center justify-between mb-4">
            <MonthYearSelector
              month={displayMonth}
              year={displayYear}
              onMonthChange={setDisplayMonth}
              onYearChange={setDisplayYear}
            />
            <div className="flex gap-2">
              <button
                onClick={handlePrevMonth}
                className="w-8 h-8 flex items-center justify-center rounded bg-[rgba(0,0,0,0.05)]"
              >
                <ChevronLeft size={16} className="text-neutral-strong" />
              </button>
              <button
                onClick={handleNextMonth}
                className="w-8 h-8 flex items-center justify-center rounded bg-[rgba(0,0,0,0.05)]"
              >
                <ChevronRight size={16} className="text-neutral-strong" />
              </button>
            </div>
          </div>

          {/* Calendar Grid */}
          <CalendarGrid
            displayYear={displayYear}
            displayMonth={displayMonth}
            selectedDate={selectedDate}
            today={today}
            minDate={today}
            onDayClick={handleDayClick}
          />

          <div className="border-t border-neutral-light/10 my-4" />

          {/* Time Wheel Selector */}
          <div className="flex flex-col items-start gap-2">
            <span className="text-[#000000] text-[14px]">Time</span>
            <TimeWheelSelector
              hour={hour}
              minute={minute}
              second={second}
              isPM={isPM}
              hasTimeSelected={hasTimeSelected}
              onSubmit={(h: number, m: number, s: number, p: boolean) => {
                setHour(h);
                setMinute(m);
                setSecond(s);
                setIsPM(p);
                setHasTimeSelected(true);
              }}
              onCancel={() => {}}
            />
          </div>

          <div className="flex gap-3 justify-end mt-[24px]">
            <Button
              onClick={() => {
                if (value) {
                  setSelectedDate(value);
                  setDisplayYear(value.getFullYear());
                  setDisplayMonth(value.getMonth());
                  const h = value.getHours();
                  setHour(h % 12 || 12);
                  setMinute(value.getMinutes());
                  setSecond(value.getSeconds());
                  setIsPM(h >= 12);
                  setHasTimeSelected(true);
                } else {
                  setSelectedDate(null);
                  setHasTimeSelected(false);
                  setHour(12);
                  setMinute(0);
                  setSecond(0);
                  setIsPM(false);
                }
                setOpen(false);
              }}
              className="h-[48px] text-[16px] text-[#000000] font-bold"
              variant={"secondary"}
            >
              Cancel
            </Button>
            <Button
              onClick={handleTimeSave}
              disabled={!selectedDate || !hasTimeSelected || isSelectedTimeInPast}
              className="h-[48px] text-[16px] font-bold"
            >
              Submit
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}


export default DateTimePicker;